/**
 * Payment Lock — Security & session utilities
 * SHA-256 validation via Web Crypto API (no plain-text passwords in theme)
 */

(function (global) {
  'use strict';

  const SESSION_KEY = 'payment_lock_session_v1';
  const DISABLE_KEY = 'payment_lock_disabled_v1';
  const AUTH_KEY = 'payment_lock_authorized_v1';
  const UNLOCK_PARAM = 'developer_unlock';
  const LOGO_CLICK_THRESHOLD = 10;

  /**
   * Hash a string with SHA-256 and return hex digest.
   * @param {string} value
   * @returns {Promise<string>}
   */
  async function sha256(value) {
    if (!global.crypto?.subtle) {
      throw new Error('Web Crypto API is unavailable in this browser.');
    }

    const encoded = new TextEncoder().encode(value);
    const digest = await global.crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Compare entered password against stored hash.
   * @param {string} password
   * @param {string} storedHash
   * @returns {Promise<boolean>}
   */
  async function validatePasswordHash(password, storedHash) {
    if (!password || !storedHash) return false;

    try {
      const inputHash = await sha256(password.trim());
      return inputHash.toLowerCase() === storedHash.trim().toLowerCase();
    } catch (error) {
      console.warn('[PaymentLock] Hash validation failed:', error);
      return false;
    }
  }

  /**
   * Placeholder license verification — replace with real API in production.
   * @param {object} config
   * @returns {Promise<boolean>}
   */
  async function verifyLicense(config = {}) {
    if (!config.licenseModeEnabled) return true;

    const apiUrl = config.licenseApiUrl;
    if (!apiUrl) {
      if (config.developerMode) {
        console.warn('[PaymentLock] License mode enabled but no API URL configured.');
      }
      return false;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) return false;
      const data = await response.json();
      return Boolean(data?.valid ?? data?.licensed ?? data?.active);
    } catch (error) {
      if (config.developerMode) {
        console.warn('[PaymentLock] License verification failed:', error);
      }
      return false;
    }
  }

  /**
   * Parse session duration from settings.
   * @param {string} duration
   * @returns {number} milliseconds
   */
  function getSessionDurationMs(duration) {
    const map = {
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
    };
    return map[duration] || map['24h'];
  }

  /**
   * Persist unlock session in localStorage.
   * @param {string} duration
   */
  function saveSession(duration) {
    const payload = {
      unlockedAt: Date.now(),
      expiresAt: Date.now() + getSessionDurationMs(duration),
    };

    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn('[PaymentLock] Could not save session:', error);
    }
  }

  /**
   * Read and validate session expiry.
   * @returns {boolean}
   */
  function hasValidSession() {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return false;

      const session = JSON.parse(raw);
      if (!session?.expiresAt) return false;

      if (Date.now() > session.expiresAt) {
        localStorage.removeItem(SESSION_KEY);
        return false;
      }

      return true;
    } catch {
      localStorage.removeItem(SESSION_KEY);
      return false;
    }
  }

  /**
   * Clear unlock session (admin reset).
   */
  function clearSession() {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch (error) {
      console.warn('[PaymentLock] Could not clear session:', error);
    }
  }

  /**
   * Permanently disable overlay on this browser until revision changes.
   * @param {string} revision
   */
  function savePermanentDisable(revision) {
    if (!revision) return;

    const payload = {
      revision,
      disabledAt: Date.now(),
    };

    try {
      localStorage.setItem(DISABLE_KEY, JSON.stringify(payload));
      localStorage.setItem(
        AUTH_KEY,
        JSON.stringify({
          revision,
          authorized: true,
          authorizedAt: Date.now(),
        })
      );
    } catch (error) {
      console.warn('[PaymentLock] Could not save permanent disable:', error);
    }
  }

  /**
   * Check if overlay was permanently disabled for current revision.
   * @param {string} revision
   * @returns {boolean}
   */
  function isPermanentlyDisabled(revision) {
    if (!revision) return false;

    try {
      const raw = localStorage.getItem(DISABLE_KEY);
      if (!raw) return false;

      const data = JSON.parse(raw);
      if (data?.revision !== revision) {
        localStorage.removeItem(DISABLE_KEY);
        return false;
      }

      return true;
    } catch {
      localStorage.removeItem(DISABLE_KEY);
      return false;
    }
  }

  /**
   * Clear permanent disable flag (admin reset).
   */
  function clearPermanentDisable() {
    try {
      localStorage.removeItem(DISABLE_KEY);
      localStorage.removeItem(AUTH_KEY);
    } catch (error) {
      console.warn('[PaymentLock] Could not clear permanent disable:', error);
    }
  }

  /**
   * Authorized removal — only set after valid disable password.
   * @param {string} revision
   * @returns {boolean}
   */
  function isAuthorizedRemoval(revision) {
    if (!revision) return false;
    if (!isPermanentlyDisabled(revision)) return false;

    try {
      const auth = JSON.parse(localStorage.getItem(AUTH_KEY) || 'null');
      return Boolean(auth?.authorized && auth?.revision === revision);
    } catch {
      return false;
    }
  }

  /**
   * Resolve password type: 'view', 'disable', or false.
   * @param {string} password
   * @param {object} hashes
   * @returns {Promise<'view'|'disable'|false>}
   */
  async function resolvePasswordType(password, hashes = {}) {
    if (!password) return false;

    if (hashes.disable && (await validatePasswordHash(password, hashes.disable))) {
      return 'disable';
    }

    if (hashes.view && (await validatePasswordHash(password, hashes.view))) {
      return 'view';
    }

    return false;
  }

  /**
   * Check URL-based developer unlock token.
   * @param {string} token
   * @param {string} expectedToken
   * @returns {boolean}
   */
  function checkUrlUnlock(token, expectedToken) {
    if (!token || !expectedToken) return false;
    return token === expectedToken;
  }

  global.PaymentLockSecurity = {
    SESSION_KEY,
    DISABLE_KEY,
    AUTH_KEY,
    UNLOCK_PARAM,
    LOGO_CLICK_THRESHOLD,
    sha256,
    validatePasswordHash,
    resolvePasswordType,
    verifyLicense,
    getSessionDurationMs,
    saveSession,
    hasValidSession,
    clearSession,
    savePermanentDisable,
    isPermanentlyDisabled,
    isAuthorizedRemoval,
    clearPermanentDisable,
    checkUrlUnlock,
  };
})(window);
